import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/** One semantic set of projects: overlapping desktop panels, native mobile rail. */
function initProjectShowcase(root: HTMLElement, reducedMotion: boolean) {
  const showcase = root.querySelector<HTMLElement>("[data-v4-showcase]");
  const stage = showcase?.querySelector<HTMLElement>("[data-work-stage]");
  const selector = showcase?.querySelector<HTMLElement>("[data-work-selector]");
  const mobileNav = showcase?.querySelector<HTMLElement>("[data-work-mobile-nav]");
  if (!showcase || !stage || !selector || !mobileNav) return () => {};
  const panels = Array.from(stage.querySelectorAll<HTMLElement>("[data-work-panel]"));
  const tabs = Array.from(selector.querySelectorAll<HTMLButtonElement>("[data-work-select]"));
  const previous = mobileNav.querySelector<HTMLButtonElement>("[data-work-prev]");
  const next = mobileNav.querySelector<HTMLButtonElement>("[data-work-next]");
  const counter = mobileNav.querySelector<HTMLElement>("[data-work-counter]");
  if (!panels.length || tabs.length !== panels.length) return () => {};
  const media = window.matchMedia("(max-width: 600px)");
  let active = Math.max(0, Math.min(panels.length - 1, Number(showcase.dataset.activeProject) || 0));
  let timeline: gsap.core.Timeline | null = null;
  let observer: IntersectionObserver | null = null;
  let resizeFrame = 0;
  let mobile = media.matches;
  const animated = panels.flatMap(panel => Array.from(panel.querySelectorAll<HTMLElement>("[data-work-copy], [data-work-visual], [data-work-media]")));

  const stopTransition = (): void => {
    timeline?.kill(); timeline = null;
    gsap.set([...panels, ...animated], { clearProps: "opacity,transform,clipPath" });
    panels.forEach(panel => panel.classList.remove("is-leaving"));
  };
  const syncStatus = (): void => {
    showcase.dataset.activeProject = String(active);
    showcase.dataset.activeTone = panels[active]!.dataset.workTone;
    tabs.forEach((tab, index) => {
      tab.setAttribute("aria-selected", String(index === active));
      tab.tabIndex = index === active ? 0 : -1;
    });
    if (counter) counter.textContent = String(active + 1).padStart(2, "0");
    if (previous) previous.disabled = active === 0;
    if (next) next.disabled = active === panels.length - 1;
  };
  const syncPanels = (): void => {
    panels.forEach((panel, index) => {
      const selected = index === active;
      panel.classList.toggle("is-active", selected);
      panel.inert = !mobile && !selected;
      if (mobile) {
        panel.removeAttribute("aria-hidden");
        panel.setAttribute("role", "group");
        panel.setAttribute("aria-labelledby", panel.querySelector("h3")!.id);
      } else {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tabs[index]!.id);
        panel.setAttribute("aria-hidden", String(!selected));
      }
    });
  };
  const scrollToProject = (index: number, smooth: boolean): void => {
    // Native horizontal scrolling only; never move the page's vertical position.
    stage.scrollTo({ left: panels[index]!.offsetLeft - panels[0]!.offsetLeft, behavior: smooth && !reducedMotion ? "smooth" : "instant" });
  };
  const select = (index: number): void => {
    if (index < 0 || index >= panels.length || index === active) return;
    const outgoing = panels[active]!;
    stopTransition();
    if (!mobile && outgoing.contains(document.activeElement)) tabs[index]!.focus({ preventScroll: true });
    active = index;
    syncStatus(); syncPanels();
    if (mobile) { scrollToProject(index, true); return; }
    if (reducedMotion) return;
    const incoming = panels[active]!;
    outgoing.classList.add("is-leaving");
    timeline = gsap.timeline({ defaults: { ease: "power3.out" }, onComplete: stopTransition })
      .to(outgoing, { opacity: 0, duration: .2 }, 0)
      .fromTo(incoming.querySelector("[data-work-visual]"), { clipPath: "inset(0 100% 0 0 round 24px)", opacity: .65 }, { clipPath: "inset(0 0% 0 0 round 24px)", opacity: 1, duration: .62 }, .04)
      .fromTo(incoming.querySelectorAll("[data-work-media]"), { scale: 1.025, x: 8 }, { scale: 1, x: 0, duration: .65 }, .04)
      .fromTo(incoming.querySelectorAll("[data-work-copy]"), { y: 9, opacity: 0 }, { y: 0, opacity: 1, duration: .38, stagger: .045 }, .12);
  };
  const selectHash = (): void => {
    const index = panels.findIndex(panel => `#${panel.id}` === window.location.hash);
    if (index >= 0) select(index);
  };
  const onClick = (event: MouseEvent): void => {
    const tab = (event.target as Element).closest<HTMLButtonElement>("[data-work-select]");
    if (tab) select(tabs.indexOf(tab));
  };
  const onKey = (event: KeyboardEvent): void => {
    const index = tabs.indexOf(event.target as HTMLButtonElement);
    if (index < 0) return;
    let target: number;
    if (event.key === "ArrowRight") target = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") target = (index + tabs.length - 1) % tabs.length;
    else if (event.key === "Home") target = 0;
    else if (event.key === "End") target = tabs.length - 1;
    else return;
    event.preventDefault();
    tabs[target]!.focus({ preventScroll: true });
    select(target);
  };
  const onPrevious = (): void => select(active - 1);
  const onNext = (): void => select(active + 1);
  const configure = (): void => {
    stopTransition(); observer?.disconnect(); observer = null;
    mobile = media.matches;
    showcase.classList.toggle("is-mobile", mobile);
    selector.hidden = mobile; mobileNav.hidden = !mobile;
    selector.setAttribute("role", "tablist");
    tabs.forEach((tab, index) => { tab.setAttribute("role", "tab"); tab.setAttribute("aria-controls", panels[index]!.id); });
    stage.setAttribute("role", mobile ? "region" : "presentation");
    if (mobile) { stage.setAttribute("aria-labelledby", "work-title"); stage.tabIndex = 0; }
    else { stage.removeAttribute("aria-labelledby"); stage.removeAttribute("tabindex"); }
    syncStatus(); syncPanels();
    showcase.classList.add("is-ready");
    if (mobile) {
      scrollToProject(active, false);
      const ratios = panels.map(() => 0);
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { ratios[panels.indexOf(entry.target as HTMLElement)] = entry.intersectionRatio; });
        const index = ratios.indexOf(Math.max(...ratios));
        if ((ratios[index] ?? 0) > .55 && index !== active) { active = index; syncStatus(); syncPanels(); }
      }, { root: stage, threshold: [.25, .55, .75, 1] });
      panels.forEach(panel => observer!.observe(panel));
    } else {
      stage.scrollLeft = 0;
      const focused = document.activeElement;
      if (panels.some((panel, index) => index !== active && panel.contains(focused))) tabs[active]!.focus({ preventScroll: true });
    }
  };
  const onResize = (): void => {
    if (!mobile) return;
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => scrollToProject(active, false));
  };
  selector.addEventListener("click", onClick);
  selector.addEventListener("keydown", onKey);
  previous?.addEventListener("click", onPrevious); next?.addEventListener("click", onNext);
  media.addEventListener("change", configure);
  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("hashchange", selectHash);
  configure(); selectHash();
  return () => {
    stopTransition(); observer?.disconnect(); cancelAnimationFrame(resizeFrame);
    selector.removeEventListener("click", onClick); selector.removeEventListener("keydown", onKey);
    previous?.removeEventListener("click", onPrevious); next?.removeEventListener("click", onNext);
    media.removeEventListener("change", configure); window.removeEventListener("resize", onResize); window.removeEventListener("hashchange", selectHash);
    showcase.classList.remove("is-ready", "is-mobile");
    selector.hidden = true; mobileNav.hidden = true;
    stage.removeAttribute("role"); stage.removeAttribute("tabindex"); stage.removeAttribute("aria-labelledby");
    panels.forEach(panel => { panel.inert = false; panel.removeAttribute("role"); panel.removeAttribute("aria-hidden"); panel.setAttribute("aria-labelledby", panel.querySelector("h3")!.id); panel.classList.remove("is-active"); });
  };
}

/** Server-rendered details: vertical desktop tabs, a single open accordion on mobile. */
function initServiceIndex(root: HTMLElement, reducedMotion: boolean) {
  const section = root.querySelector<HTMLElement>("[data-v4-services]");
  const index = section?.querySelector<HTMLElement>("[data-service-index]");
  const tablist = section?.querySelector<HTMLElement>("[data-service-tabs]");
  const indicator = section?.querySelector<HTMLElement>("[data-service-indicator]");
  if (!section || !index || !tablist || !indicator) return () => {};
  const items = Array.from(section.querySelectorAll<HTMLDetailsElement>("[data-service-item]"));
  const tabs = Array.from(tablist.querySelectorAll<HTMLButtonElement>("[data-service-select]"));
  const panels = items.map(item => item.querySelector<HTMLElement>("[data-service-panel]")!);
  const summaries = items.map(item => item.querySelector<HTMLElement>("summary")!);
  if (!items.length || items.length !== tabs.length || panels.some(panel => !panel)) return () => {};
  const copy = panels.flatMap(panel => Array.from(panel.querySelectorAll<HTMLElement>("[data-service-copy]")));
  const media = window.matchMedia("(max-width: 700px)");
  let mobile = media.matches;
  let active = Math.max(0, Math.min(items.length - 1, Number(section.dataset.activeService) || 0));
  let expanded = section.dataset.expandedService === undefined ? -1 : Number(section.dataset.expandedService);
  if (!Number.isInteger(expanded) || expanded < -1 || expanded >= items.length) expanded = -1;
  let transition: gsap.core.Timeline | null = null;
  let indicatorTween: gsap.core.Tween | null = null;
  let resizeFrame = 0;

  const clearMotion = (): void => {
    transition?.kill(); transition = null;
    gsap.set([...items, ...panels, ...copy], { clearProps: "height,overflow,opacity,transform" });
    items.forEach(item => item.classList.remove("is-leaving"));
  };
  const moveIndicator = (animate: boolean): void => {
    indicatorTween?.kill();
    if (mobile) return;
    const tab = tabs[active]!;
    // Measurements happen only on selection / resize, never in a scroll loop.
    const position = { y: tab.offsetTop, height: tab.offsetHeight };
    if (animate && !reducedMotion) indicatorTween = gsap.to(indicator, { ...position, duration: .45, ease: "power3.inOut" });
    else gsap.set(indicator, position);
  };
  const sync = (): void => {
    section.dataset.activeService = String(active);
    section.dataset.expandedService = String(expanded);
    tabs.forEach((tab, i) => { tab.setAttribute("aria-selected", String(i === active)); tab.tabIndex = i === active ? 0 : -1; });
    items.forEach((item, i) => {
      const selected = mobile ? i === expanded : i === active;
      item.classList.toggle("is-active", selected);
      item.open = mobile ? selected : true;
      item.inert = !mobile && !selected;
      if (mobile) item.removeAttribute("aria-hidden");
      else item.setAttribute("aria-hidden", String(!selected));
      panels[i]!.inert = !selected;
      panels[i]!.setAttribute("aria-hidden", String(!selected));
      if (mobile) summaries[i]!.setAttribute("aria-expanded", String(selected));
    });
  };
  const select = (next: number): void => {
    if (next < 0 || next >= items.length || (!mobile && next === active)) return;
    const outgoing = items[active]!;
    // Read every current accordion height before writing to any item.
    const heights = mobile ? panels.map((panel, i) => items[i]!.open ? panel.getBoundingClientRect().height : 0) : [];
    clearMotion();
    if (mobile) expanded = expanded === next ? -1 : next;
    active = next;
    sync(); moveIndicator(true);
    if (reducedMotion) { if (mobile) ScrollTrigger.refresh(); return; }
    transition = gsap.timeline({ defaults: { ease: "power3.out" }, onComplete: () => {
      clearMotion(); sync(); if (mobile) ScrollTrigger.refresh();
    } });
    if (mobile) {
      items.forEach((item, i) => {
        const start = heights[i] ?? 0;
        if (start === 0 && i !== expanded) return;
        item.open = true; // Keep the outgoing surface measurable until it closes.
        const end = i === expanded ? panels[i]!.scrollHeight : 0;
        transition!.fromTo(panels[i]!, { height: start, overflow: "hidden" }, { height: end, duration: .45 }, 0);
      });
      if (expanded >= 0) transition.fromTo(panels[expanded]!.querySelectorAll("[data-service-copy]"), { y: 6, opacity: 0 }, { y: 0, opacity: 1, duration: .35, stagger: .035 }, .04);
    } else {
      outgoing.classList.add("is-leaving");
      transition.to(outgoing, { opacity: 0, duration: .2 }, 0)
        .fromTo(panels[active]!.querySelectorAll("[data-service-copy]"), { y: 8, opacity: 0 }, { y: 0, opacity: 1, duration: .38, stagger: .035 }, .04);
    }
  };
  const onClick = (event: MouseEvent): void => {
    const target = event.target as Element;
    const tab = target.closest<HTMLButtonElement>("[data-service-select]");
    const summary = target.closest<HTMLElement>("summary");
    if (!mobile && tab) select(tabs.indexOf(tab));
    if (mobile && summary && summaries.includes(summary)) { event.preventDefault(); select(summaries.indexOf(summary)); }
  };
  const onKey = (event: KeyboardEvent): void => {
    const current = tabs.indexOf(event.target as HTMLButtonElement);
    if (mobile || current < 0) return;
    const next = event.key === "ArrowDown" ? (current + 1) % tabs.length
      : event.key === "ArrowUp" ? (current + tabs.length - 1) % tabs.length
      : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : -1;
    if (next < 0) return;
    event.preventDefault(); tabs[next]!.focus({ preventScroll: true }); select(next);
  };
  const configure = (): void => {
    const focused = document.activeElement;
    clearMotion(); mobile = media.matches;
    section.classList.toggle("is-mobile", mobile);
    index.hidden = mobile;
    tablist.setAttribute("role", "tablist"); tablist.setAttribute("aria-orientation", "vertical");
    items.forEach((item, i) => {
      // Native exclusivity is the fallback; JS briefly opens two items while animating.
      item.removeAttribute("name");
      tabs[i]!.setAttribute("role", "tab"); tabs[i]!.setAttribute("aria-controls", panels[i]!.id);
      panels[i]!.setAttribute("role", mobile ? "region" : "tabpanel");
      panels[i]!.setAttribute("aria-labelledby", mobile ? summaries[i]!.id : tabs[i]!.id);
      summaries[i]!.setAttribute("aria-controls", panels[i]!.id);
      summaries[i]!.removeAttribute("aria-expanded");
    });
    section.classList.add("is-ready"); sync(); moveIndicator(false);
    if (mobile && tabs.includes(focused as HTMLButtonElement)) summaries[active]!.focus({ preventScroll: true });
    if (!mobile && summaries.includes(focused as HTMLElement)) tabs[active]!.focus({ preventScroll: true });
    if (!mobile && items.some((item, i) => i !== active && item.contains(focused))) tabs[active]!.focus({ preventScroll: true });
  };
  const onResize = (): void => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => { clearMotion(); sync(); moveIndicator(false); });
  };
  section.addEventListener("click", onClick); tablist.addEventListener("keydown", onKey);
  media.addEventListener("change", configure); window.addEventListener("resize", onResize, { passive: true });
  configure();
  return () => {
    clearMotion(); indicatorTween?.kill(); cancelAnimationFrame(resizeFrame);
    section.removeEventListener("click", onClick); tablist.removeEventListener("keydown", onKey);
    media.removeEventListener("change", configure); window.removeEventListener("resize", onResize);
    section.classList.remove("is-ready", "is-mobile"); index.hidden = true;
    items.forEach((item, i) => {
      item.inert = false; item.removeAttribute("aria-hidden"); item.classList.remove("is-active"); item.open = false; item.setAttribute("name", "studio-services");
      panels[i]!.inert = false; panels[i]!.removeAttribute("role"); panels[i]!.removeAttribute("aria-hidden"); panels[i]!.removeAttribute("aria-labelledby");
      summaries[i]!.removeAttribute("aria-expanded"); summaries[i]!.removeAttribute("aria-controls");
    });
  };
}


/** Phase 4 is decorative only: content and navigation stay available without motion. */
function initPhase4Motion(root: HTMLElement, reducedMotion: boolean) {
  if (reducedMotion) return () => {};
  const context = gsap.context(() => {
    root.querySelectorAll<HTMLElement>("[data-phase4]").forEach(section => {
      gsap.timeline({ scrollTrigger: { trigger: section, start: "top 86%", once: true } })
        .fromTo(section.querySelectorAll("[data-phase4-reveal]"), { y: 14, opacity: 0 }, {
          y: 0, opacity: 1, duration: .65, stagger: .075, ease: "power3.out", clearProps: "transform,opacity",
        }, 0);
      const lines = section.querySelectorAll("[data-phase4-line]");
      if (lines.length) gsap.fromTo(lines, { scaleX: 0 }, {
        scaleX: 1, duration: .85, stagger: .08, ease: "power2.out", clearProps: "transform",
        scrollTrigger: { trigger: section, start: "top 80%", once: true },
      });
    });
    const progress = root.querySelector<HTMLElement>("[data-method-progress]");
    const timeline = root.querySelector<HTMLElement>("[data-method-timeline]");
    if (!progress || !timeline) return;
    const media = gsap.matchMedia();
    media.add({ desktop: "(min-width: 901px)", compact: "(max-width: 900px)" }, mediaContext => {
      const horizontal = Boolean(mediaContext.conditions?.desktop);
      gsap.fromTo(progress, { scaleX: horizontal ? 0 : 1, scaleY: horizontal ? 1 : 0 }, {
        scaleX: 1, scaleY: 1, ease: "none",
        scrollTrigger: { trigger: timeline, start: "top 80%", end: horizontal ? "bottom 55%" : "bottom 70%", scrub: .45, invalidateOnRefresh: true },
      });
    });
  }, root);
  return () => context.revert();
}


/** Presentation only: native validation and the existing POST remain in charge. */
function initPhase5Presentation(root: HTMLElement, reducedMotion: boolean) {
  const cleanup: Array<() => void> = [];
  const form = root.querySelector<HTMLFormElement>("[data-phase5-form]");
  const button = form?.querySelector<HTMLButtonElement>("[data-form-submit]");
  const label = button?.querySelector<HTMLElement>("[data-submit-label]");
  const validation = form?.querySelector<HTMLElement>("[data-form-validation]");
  if (form && button && label && validation) {
    const idleLabel = label.textContent;
    const resetPending = (): void => {
      form.removeAttribute("aria-busy");
      button.disabled = false;
      label.textContent = idleLabel;
    };
    const onSubmit = (event: SubmitEvent): void => {
      if (event.defaultPrevented) return;
      // Disable only the unnamed submit control; all POST fields stay successful.
      form.setAttribute("aria-busy", "true");
      button.disabled = true;
      label.textContent = button.dataset.loadingLabel ?? idleLabel;
    };
    const onInvalid = (event: Event): void => {
      const field = event.target;
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
        field.setAttribute("aria-invalid", "true");
        validation.hidden = false;
      }
    };
    const onInput = (event: Event): void => {
      const field = event.target;
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
        if (field.validity.valid) field.removeAttribute("aria-invalid");
        if (!form.querySelector(":invalid")) validation.hidden = true;
      }
    };
    form.addEventListener("submit", onSubmit);
    form.addEventListener("invalid", onInvalid, true);
    form.addEventListener("input", onInput);
    form.addEventListener("change", onInput);
    window.addEventListener("pageshow", resetPending);
    cleanup.push(() => {
      form.removeEventListener("submit", onSubmit);
      form.removeEventListener("invalid", onInvalid, true);
      form.removeEventListener("input", onInput);
      form.removeEventListener("change", onInput);
      window.removeEventListener("pageshow", resetPending);
      resetPending();
    });
  }
  if (!reducedMotion) {
    const context = gsap.context(() => {
      root.querySelectorAll<HTMLElement>("[data-phase5-reveal]").forEach(element => {
        // Content is visible by default, including without JS and before entry.
        ScrollTrigger.create({ trigger: element, start: "top 94%", once: true, onEnter: () => {
          gsap.fromTo(element, { y: 12, opacity: .65 }, {
            y: 0, opacity: 1, duration: .65, ease: "power3.out", clearProps: "transform,opacity",
          });
        } });
        const onFocus = (): void => {
          // Keyboard users never have to wait for a reveal to finish.
          gsap.getTweensOf(element).forEach(tween => tween.progress(1));
        };
        element.addEventListener("focusin", onFocus);
        cleanup.push(() => element.removeEventListener("focusin", onFocus));
      });
    }, root);
    cleanup.push(() => context.revert());
  }
  return () => cleanup.reverse().forEach(remove => remove());
}

export function initV4Motion(reducedMotion: boolean) {
  const root = document.querySelector<HTMLElement>("[data-v4-home]");
  if (!root) return { destroy() {} };
  const cleanup: Array<() => void> = [];
  cleanup.push(initProjectShowcase(root, reducedMotion));
  cleanup.push(initServiceIndex(root, reducedMotion));
  cleanup.push(initPhase4Motion(root, reducedMotion));
  cleanup.push(initPhase5Presentation(root, reducedMotion));
  const ticker = root.querySelector<HTMLElement>("[data-v4-ticker]");
  const toggle = root.querySelector<HTMLButtonElement>("[data-v4-ticker-toggle]");
  const onToggle = (): void => {
    const paused = ticker?.classList.toggle("is-paused") ?? false;
    toggle?.setAttribute("aria-pressed", String(paused));
    toggle?.setAttribute("aria-label", paused ? toggle.dataset.playLabel ?? "Play ticker" : toggle.dataset.pauseLabel ?? "Pause ticker");
    const icon = toggle?.querySelector("span");
    if (icon) icon.textContent = paused ? "▷" : "Ⅱ";
  };
  toggle?.addEventListener("click", onToggle);
  cleanup.push(() => toggle?.removeEventListener("click", onToggle));
  let tickerVisible = false;
  const syncTicker = (): void => { ticker?.classList.toggle("is-suspended", !tickerVisible || document.hidden); };
  if (ticker) {
    const observer = new IntersectionObserver(([entry]) => { tickerVisible = Boolean(entry?.isIntersecting); syncTicker(); });
    observer.observe(ticker);
    document.addEventListener("visibilitychange", syncTicker);
    cleanup.push(() => { observer.disconnect(); document.removeEventListener("visibilitychange", syncTicker); });
  }
  if (reducedMotion) return { destroy() { cleanup.reverse().forEach(remove => remove()); } };

  const context = gsap.context(() => {
    gsap.timeline({ defaults: { ease: "power3.out" } })
      .fromTo("[data-v4-title] i", { yPercent: 110 }, { yPercent: 0, duration: 1.15, stagger: .105, clearProps: "transform" }, .08)
      .fromTo("[data-v4-intro]", { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: .85, stagger: .06, clearProps: "transform,opacity" }, .45)
      .fromTo("[data-v4-core-art]", { y: 25, opacity: 0 }, { y: 0, opacity: 1, duration: 1.3, clearProps: "transform,opacity" }, .2);

    root.querySelectorAll<HTMLElement>("[data-v4-reveal]").forEach(element => {
      ScrollTrigger.create({ trigger: element, start: "top 94%", once: true, onEnter: () => {
        gsap.fromTo(element, { y: 22, opacity: 0 }, { y: 0, opacity: 1, duration: .85, ease: "power2.out", clearProps: "transform,opacity" });
      } });
    });
    root.querySelectorAll<HTMLElement>("[data-v4-image]").forEach(element => {
      ScrollTrigger.create({ trigger: element, start: "top 94%", once: true, onEnter: () => {
        gsap.fromTo(element, { clipPath: "inset(7% 0 0 0 round 24px)", opacity: .8 }, { clipPath: "inset(0% 0 0 0 round 24px)", opacity: 1, duration: 1.1, ease: "power3.out", clearProps: "clipPath,opacity" });
      } });
    });
    root.querySelectorAll<HTMLElement>("[data-v4-problem]").forEach(element => {
      ScrollTrigger.create({ trigger: element, start: "top 66%", end: "bottom 42%", toggleClass: "is-active" });
    });
    const media = gsap.matchMedia();
    media.add("(min-width: 1061px) and (pointer: fine)", () => {
      root.querySelectorAll<HTMLElement>("[data-v4-parallax]").forEach(element => {
        gsap.fromTo(element, { y: 10 }, { y: -10, ease: "none", scrollTrigger: { trigger: element.closest(".v4-project-stage"), start: "top bottom", end: "bottom top", scrub: .7 } });
      });
      const removes: Array<() => void> = [];
      root.querySelectorAll<HTMLElement>("[data-v4-magnetic]").forEach(button => {
        let centerX = 0, centerY = 0;
        const x = gsap.quickTo(button, "x", { duration: .4, ease: "power3.out" });
        const y = gsap.quickTo(button, "y", { duration: .4, ease: "power3.out" });
        const enter = (): void => { const bounds = button.getBoundingClientRect(); centerX = bounds.left + bounds.width / 2; centerY = bounds.top + bounds.height / 2; };
        const move = (event: PointerEvent): void => { x(gsap.utils.clamp(-5, 5, (event.clientX - centerX) * .05)); y(gsap.utils.clamp(-4, 4, (event.clientY - centerY) * .07)); };
        const leave = (): void => { x(0); y(0); };
        button.addEventListener("pointerenter", enter); button.addEventListener("pointermove", move); button.addEventListener("pointerleave", leave);
        removes.push(() => { button.removeEventListener("pointerenter", enter); button.removeEventListener("pointermove", move); button.removeEventListener("pointerleave", leave); });
      });
      return () => removes.forEach(remove => remove());
    });
  }, root);

  return { destroy() { context.revert(); cleanup.reverse().forEach(remove => remove()); } };
}
